/**
 * Input validation utilities for mock handlers
 * Validates query parameters, UUIDs, dates, etc.
 */

/**
 * UUID format regex (v4)
 */
const UUID_REGEX = /^[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i

/**
 * Common ID format regex (various formats: tx_123, merch_abc, etc.)
 */
const ID_REGEX = /^[a-z_]{1,10}[\da-f\-_]{3,50}$/i

/**
 * Numeric ID regex
 */
const NUMERIC_ID_REGEX = /^\d{1,20}$/

/**
 * ISO 8601 date format regex
 */
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:?\d{2})?)?$/

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean
  errors: Array<{
    field: string
    message: string
    value?: unknown
  }>
}

/**
 * Create a successful validation result
 */
export function validResult(): ValidationResult {
  return { valid: true, errors: [] }
}

/**
 * Create a failed validation result
 */
export function invalidResult(
  field: string,
  message: string,
  value?: unknown
): ValidationResult {
  return {
    valid: false,
    errors: [{ field, message, value }]
  }
}

/**
 * Merge multiple validation results
 */
export function mergeValidationResults(...results: ValidationResult[]): ValidationResult {
  const errors = results.flatMap(r => r.errors)
  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Validate a UUID format
 */
export function isValidUuid(value: string): boolean {
  return UUID_REGEX.test(value)
}

/**
 * Validate a generic ID format (accepts various formats)
 */
export function isValidId(value: string): boolean {
  return ID_REGEX.test(value) || NUMERIC_ID_REGEX.test(value) || UUID_REGEX.test(value)
}

/**
 * Validate an ISO date string
 */
export function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_REGEX.test(value)) return false

  const date = new Date(value)
  return !Number.isNaN(date.getTime())
}

/**
 * Validate a date string (more lenient, accepts various formats)
 */
export function isValidDate(value: string): boolean {
  const date = new Date(value)
  return !Number.isNaN(date.getTime())
}

/**
 * Validate a positive integer
 */
export function isPositiveInteger(value: unknown): value is number {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0
  }
  if (typeof value === 'string') {
    const num = Number.parseInt(value, 10)
    return !Number.isNaN(num) && num > 0 && String(num) === value
  }
  return false
}

/**
 * Validate a non-negative number
 */
export function isNonNegativeNumber(value: unknown): boolean {
  if (typeof value === 'number') {
    return !Number.isNaN(value) && value >= 0
  }
  if (typeof value === 'string') {
    const num = Number.parseFloat(value)
    return !Number.isNaN(num) && num >= 0
  }
  return false
}

/**
 * Validate pagination parameters
 */
export function validatePaginationParams(
  page?: string | null,
  limit?: string | null
): ValidationResult {
  const errors: ValidationResult['errors'] = []

  if (page !== undefined && page !== null && page !== '') {
    const pageNum = Number.parseInt(page, 10)
    if (Number.isNaN(pageNum) || pageNum < 1) {
      errors.push({ field: 'page', message: 'Page must be a positive integer', value: page })
    }
  }

  if (limit !== undefined && limit !== null && limit !== '') {
    const limitNum = Number.parseInt(limit, 10)
    if (Number.isNaN(limitNum) || limitNum < 1) {
      errors.push({ field: 'limit', message: 'Limit must be a positive integer', value: limit })
    } else if (limitNum > 100) {
      errors.push({ field: 'limit', message: 'Limit cannot exceed 100', value: limit })
    }
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate date range parameters
 */
export function validateDateRange(
  dateFrom?: string | null,
  dateTo?: string | null
): ValidationResult {
  const errors: ValidationResult['errors'] = []

  if (dateFrom && !isValidDate(dateFrom)) {
    errors.push({ field: 'dateFrom', message: 'Invalid date format', value: dateFrom })
  }

  if (dateTo && !isValidDate(dateTo)) {
    errors.push({ field: 'dateTo', message: 'Invalid date format', value: dateTo })
  }

  // Check that dateFrom is before dateTo
  if (dateFrom && dateTo && isValidDate(dateFrom) && isValidDate(dateTo)) {
    const from = new Date(dateFrom)
    const to = new Date(dateTo)
    if (from > to) {
      errors.push({
        field: 'dateRange',
        message: 'dateFrom must be before dateTo',
        value: { dateFrom, dateTo }
      })
    }
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate amount range parameters
 */
export function validateAmountRange(
  amountMin?: string | null,
  amountMax?: string | null
): ValidationResult {
  const errors: ValidationResult['errors'] = []

  let minValue: number | undefined
  let maxValue: number | undefined

  if (amountMin) {
    minValue = Number.parseFloat(amountMin)
    if (Number.isNaN(minValue) || minValue < 0) {
      errors.push({ field: 'amountMin', message: 'Amount must be a non-negative number', value: amountMin })
    }
  }

  if (amountMax) {
    maxValue = Number.parseFloat(amountMax)
    if (Number.isNaN(maxValue) || maxValue < 0) {
      errors.push({ field: 'amountMax', message: 'Amount must be a non-negative number', value: amountMax })
    }
  }

  // Check that amountMin is less than or equal to amountMax
  if (minValue !== undefined && maxValue !== undefined && minValue > maxValue) {
    errors.push({
      field: 'amountRange',
      message: 'amountMin must be less than or equal to amountMax',
      value: { amountMin, amountMax }
    })
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Validate a resource ID parameter
 */
export function validateResourceId(
  id: string | undefined,
  resourceType: string
): ValidationResult {
  if (!id) {
    return invalidResult('id', `${resourceType} ID is required`)
  }

  if (!isValidId(id)) {
    return invalidResult('id', `Invalid ${resourceType} ID format`, id)
  }

  return validResult()
}

/**
 * Validate sort parameters
 */
export function validateSortParams(
  sortBy?: string | null,
  sortOrder?: string | null,
  allowedFields?: string[]
): ValidationResult {
  const errors: ValidationResult['errors'] = []

  if (sortBy && allowedFields && !allowedFields.includes(sortBy)) {
    errors.push({
      field: 'sortBy',
      message: `Invalid sort field. Allowed fields: ${allowedFields.join(', ')}`,
      value: sortBy
    })
  }

  if (sortOrder) {
    const normalizedOrder = sortOrder.toLowerCase()
    if (normalizedOrder !== 'asc' && normalizedOrder !== 'desc') {
      errors.push({
        field: 'sortOrder',
        message: 'Sort order must be "asc" or "desc"',
        value: sortOrder
      })
    }
  }

  return { valid: errors.length === 0, errors }
}

/**
 * Parse and validate query parameters for list endpoints
 */
export interface ListQueryParams {
  page: number
  limit: number
  search?: string
  status?: string
  type?: string
  dateFrom?: string
  dateTo?: string
  amountMin?: number
  amountMax?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export function parseListQueryParams(
  searchParams: URLSearchParams,
  options: {
    allowedSortFields?: string[]
    defaultSortBy?: string
    defaultSortOrder?: 'asc' | 'desc'
  } = {}
): { params: ListQueryParams, validation: ValidationResult } {
  // Parse all parameters
  const page = searchParams.get('page')
  const limit = searchParams.get('limit')
  const search = searchParams.get('search')
  const status = searchParams.get('status')
  const type = searchParams.get('type') || searchParams.get('type_charge')
  const dateFrom = searchParams.get('dateFrom')
  const dateTo = searchParams.get('dateTo')
  const amountMin = searchParams.get('amountMin')
  const amountMax = searchParams.get('amountMax')
  const sortBy = searchParams.get('sortBy') || searchParams.get('sort_by')
  const sortOrder = searchParams.get('sortOrder') || searchParams.get('sort_order')

  // Validate
  const validation = mergeValidationResults(
    validatePaginationParams(page, limit),
    validateDateRange(dateFrom, dateTo),
    validateAmountRange(amountMin, amountMax),
    validateSortParams(sortBy, sortOrder, options.allowedSortFields)
  )

  // Parse validated values
  const params: ListQueryParams = {
    page: page ? Number.parseInt(page, 10) : 1,
    limit: limit ? Number.parseInt(limit, 10) : 25,
    search: search || undefined,
    status: status || undefined,
    type: type || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    amountMin: amountMin ? Number.parseFloat(amountMin) : undefined,
    amountMax: amountMax ? Number.parseFloat(amountMax) : undefined,
    sortBy: sortBy || options.defaultSortBy,
    sortOrder: (sortOrder?.toLowerCase() as 'asc' | 'desc') || options.defaultSortOrder || 'desc'
  }

  return { params, validation }
}
